import Vue from 'vue';
import Router from 'vue-router';
import store from '../store/store.js';

Vue.use(Router);

// helper function to add prefix to given routes and route names
const dayRoute = (routes, week, day) => {
    routes.map(route => {
        route.path = `/game/days/w${week}d${day}/${route.name}`;
        route.name = `w${week}d${day}${route.name}`;
        route.meta = {
            ...route.meta,
            dayRoute: true,
            week: week,
            day: day
        };

        return route;
    });

    return routes;
};

/*
 * check if browser back/next button is clicked.
 * This needs to be initialized before Router instance is created
 */
let backbuttonPressed = false;
window.onpopstate = () => {
    backbuttonPressed = true;
};

/*
 * Game day routes.
 * Route array order is important for game/next route!
 */
import w1d1 from './routes/w1d1.js';
import w1d2 from './routes/w1d2.js';
import w1d3 from './routes/w1d3.js';
import w1d4 from './routes/w1d4.js';
import w1d5 from './routes/w1d5.js';

import w2d1 from './routes/w2d1.js';
import w2d2 from './routes/w2d2.js';
import w2d3 from './routes/w2d3.js';
import w2d4 from './routes/w2d4.js';
import w2d5 from './routes/w2d5.js';

import w3d1 from './routes/w3d1.js';
import w3d2 from './routes/w3d2.js';
import w3d3 from './routes/w3d3.js';
import w3d4 from './routes/w3d4.js';
import w3d5 from './routes/w3d5.js';

import w4d1 from './routes/w4d1.js';
import w4d2 from './routes/w4d2.js';
import w4d3 from './routes/w4d3.js';
import w4d4 from './routes/w4d4.js';
import w4d5 from './routes/w4d5.js';

const router = new Router({
    mode: 'history',

    routes: [
        {
            path: '/game/onboarding',
            name: 'onboarding',
            component: () => import('../../components/game/GameOnboarding.vue')
        },
        {
            path: '/game/cockpit',
            name: 'cockpit',
            component: () => import('../../components/game/GameCockpit.vue')
        },
        {
            path: '/game/surprises',
            name: 'surprises',
            component: () => import('../../components/game/surprises/GameSurprises.vue')
        },
        {
            path: '/game/help/:gameroute?',
            name: 'help',
            component: () => import('../../components/game/help/GameHelp.vue')
        },
        {
            path: '/game/shop',
            name: 'shop',
            component: () => import('../../components/game/GameShop.vue')
        },
        {
            path: '/game/achievements',
            name: 'achievements',
            component: () => import('../../components/game/achievements/GameAchievements.vue')
        },
        {
            path: '/game/certificate',
            name: 'certificate',
            component: () => import('../../components/game/GameCertificate.vue')
        },
        {
            path: '/game/next',
            name: 'next'
        },
        {
            path: '/game/current',
            name: 'current'
        },
        ...dayRoute(w1d1, 1, 1),
        ...dayRoute(w1d2, 1, 2),
        ...dayRoute(w1d3, 1, 3),
        ...dayRoute(w1d4, 1, 4),
        ...dayRoute(w1d5, 1, 5),

        ...dayRoute(w2d1, 2, 1),
        ...dayRoute(w2d2, 2, 2),
        ...dayRoute(w2d3, 2, 3),
        ...dayRoute(w2d4, 2, 4),
        ...dayRoute(w2d5, 2, 5),

        ...dayRoute(w3d1, 3, 1),
        ...dayRoute(w3d2, 3, 2),
        ...dayRoute(w3d3, 3, 3),
        ...dayRoute(w3d4, 3, 4),
        ...dayRoute(w3d5, 3, 5),

        ...dayRoute(w4d1, 4, 1),
        ...dayRoute(w4d2, 4, 2),
        ...dayRoute(w4d3, 4, 3),
        ...dayRoute(w4d4, 4, 4),
        ...dayRoute(w4d5, 4, 5)
    ]
});

router.beforeEach(async (to, from, next) => {
    /**
     * Prevent clicking of back button.
     */
    if (backbuttonPressed) {
        backbuttonPressed = false;
        next(false);
        return;
    }

    /**
     * Store prev route.
     */
    if (from && from.path) {
        store.commit('storePrevRoute', from.path);
    }

    /**
     * Always go to onboarding route if wanted.
     */
    if (to.name === 'onboarding' || to.name === null) {
        next();
        return;
    }

    /**
     * Load game data if not available. This is necessary before going to other routes than onboarding.
     */
    if (!store.getters.isGameDataAvailable) {
        let response = await axios.get('game/data');

        if (response && response.data && response.data.data) {
            store.commit('storeGameData', response.data.data);
        }
    }

    /**
     * Check if avatar and nickname of user is set.
     */
    if (store.getters.avatar === '' || store.getters.nickname === '') {
        next({ name: 'onboarding' });
        return;
    }

    /**
     * Check is user is allowed to visit current route.
     */
    if (to.meta && to.meta.dayRoute && !store.getters.isDayUnlocked(to.meta.week, to.meta.day)) {
        next({ name: 'cockpit' });
        return;
    }

    /**
     * Store current route when navigating to dayroute.
     */
    if (to.meta && to.meta.dayRoute) {
        let response = await axios.post('users/route', {
            current_route: to.name,
            week: to.meta.week
        });

        if (response && response.data && response.data.success) {
            store.commit('storeCurrentRoute', to.name);
        }
    }

    /**
     * Go to current dayroute.
     */
    if (to.name === 'current') {
        next({
            name: store.getters.currentRoute
        });
    }

    /**
     * Get next game day route.
     */
    if (to.name === 'next') {
        let currentRouteIndex = router.options.routes.findIndex(route => route.name === from.name);
        let nextRoute = router.options.routes[currentRouteIndex + 1];

        if (nextRoute && nextRoute.name) {
            next({ name: nextRoute.name });
        } else {
            next(false);
        }
        return;
    }

    next();
});

export default router;
